(() => {
  const SELECTOR = 'price-range.facets__price';
  const GAP = 1;

  function toNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;

    let str = String(value).trim();

    if (!str) return fallback;

    str = str.replace(/\s+/g, '').replace(/[^\d.,-]/g, '');

    const hasComma = str.includes(',');
    const hasDot = str.includes('.');

    if (hasComma && hasDot) {
      str = str.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
      str = str.replace(',', '.');
    } else {
      const dotParts = str.split('.');
      if (dotParts.length > 2) {
        const decimalPart = dotParts.pop();
        str = dotParts.join('') + '.' + decimalPart;
      }
    }

    const parsed = parseFloat(str);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function formatInputValue(value) {
    const rounded = Math.round(Number(value || 0) * 100) / 100;
    return String(rounded);
  }

  function formatPrice(value, symbol) {
    const safeValue = Number(value || 0);
    const formatted = safeValue.toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${symbol}${formatted}`;
  }

  function updateFill(fillEl, minValue, maxValue, minAllowed, maxAllowed) {
    const total = maxAllowed - minAllowed || 1;
    const left = ((minValue - minAllowed) / total) * 100;
    const right = ((maxValue - minAllowed) / total) * 100;

    fillEl.style.left = `${Math.max(0, left)}%`;
    fillEl.style.width = `${Math.max(right - left, 0)}%`;
  }

  function getContextMaxText(priceRange) {
    const container =
      priceRange.closest('.facets__display-vertical') ||
      priceRange.closest('.facets__display') ||
      priceRange.closest('.mobile-facets__submenu') ||
      priceRange.parentElement;

    if (!container) return '';

    const infoEl =
      container.querySelector('.mobile-facets__info') ||
      container.querySelector('.facets__selected');

    return infoEl ? infoEl.textContent || '' : '';
  }

  function resolveMaxAllowed(minInput, maxInput, priceRange, minAllowed) {
    const fromMaxAttr = toNumber(maxInput?.max, 0);
    const fromPlaceholder = toNumber(maxInput?.placeholder, 0);
    const fromText = toNumber(getContextMaxText(priceRange), 0);

    return Math.max(fromMaxAttr, fromPlaceholder, fromText, minAllowed + GAP);
  }

  function initPriceSlider(priceRange) {
    if (!priceRange || priceRange.dataset.sliderReady === 'true') return;

    const existingSlider = priceRange.querySelector('.facets-price-slider');
    if (existingSlider) {
      priceRange.dataset.sliderReady = 'true';
      return;
    }

    const inputs = priceRange.querySelectorAll('input.field__input');
    if (inputs.length < 2) return;

    const minInput = inputs[0];
    const maxInput = inputs[1];

    const currencySymbol = (priceRange.querySelector('.field-currency')?.textContent || '€').trim();

    const minAllowed = toNumber(minInput.min, 0);
    const maxAllowed = resolveMaxAllowed(minInput, maxInput, priceRange, minAllowed);

    let currentMin = toNumber(minInput.value, minAllowed);
    let currentMax = toNumber(maxInput.value, maxAllowed);

    currentMin = Math.max(minAllowed, Math.min(currentMin, maxAllowed - GAP));
    currentMax = Math.min(maxAllowed, Math.max(currentMax, minAllowed + GAP));

    if (currentMax <= currentMin) {
      currentMax = Math.min(maxAllowed, currentMin + GAP);
    }

    priceRange.classList.add('price-range--slider');

    const slider = document.createElement('div');
    slider.className = 'facets-price-slider';
    slider.innerHTML = `
      <div class="facets-price-slider__values">
        <div class="facets-price-slider__value" data-min-label></div>
        <div class="facets-price-slider__value" data-max-label></div>
      </div>

      <div class="facets-price-slider__range-wrap">
        <div class="facets-price-slider__track"></div>
        <div class="facets-price-slider__fill" data-fill></div>

        <input
          class="facets-price-slider__range facets-price-slider__range--min"
          data-min-range
          type="range"
          min="${minAllowed}"
          max="${maxAllowed}"
          step="0.01"
          value="${currentMin}"
        >

        <input
          class="facets-price-slider__range facets-price-slider__range--max"
          data-max-range
          type="range"
          min="${minAllowed}"
          max="${maxAllowed}"
          step="0.01"
          value="${currentMax}"
        >
      </div>
    `;

    priceRange.appendChild(slider);

    const minLabel = slider.querySelector('[data-min-label]');
    const maxLabel = slider.querySelector('[data-max-label]');
    const fill = slider.querySelector('[data-fill]');
    const minRange = slider.querySelector('[data-min-range]');
    const maxRange = slider.querySelector('[data-max-range]');

    function getMinRangeValue() {
      return toNumber(minRange.value, minAllowed);
    }

    function getMaxRangeValue() {
      return toNumber(maxRange.value, maxAllowed);
    }

    function updateUI() {
      const minValue = getMinRangeValue();
      const maxValue = getMaxRangeValue();

      minLabel.textContent = `Da ${formatPrice(minValue, currencySymbol)}`;
      maxLabel.textContent = `A ${formatPrice(maxValue, currencySymbol)}`;

      updateFill(fill, minValue, maxValue, minAllowed, maxAllowed);
    }

    function syncHiddenInputs(shouldNotify = false) {
      const minValue = getMinRangeValue();
      const maxValue = getMaxRangeValue();

      minInput.value = minValue <= minAllowed ? '' : formatInputValue(minValue);
      maxInput.value = maxValue >= maxAllowed ? '' : formatInputValue(maxValue);

      if (shouldNotify) {
        minInput.dispatchEvent(new Event('input', { bubbles: true }));
        maxInput.dispatchEvent(new Event('input', { bubbles: true }));
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
        maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function onMinInput() {
      let minValue = getMinRangeValue();
      let maxValue = getMaxRangeValue();

      if (minValue >= maxValue) {
        minValue = Math.max(minAllowed, maxValue - GAP);
        minRange.value = String(minValue);
      }

      updateUI();
      syncHiddenInputs(false);
    }

    function onMaxInput() {
      let minValue = getMinRangeValue();
      let maxValue = getMaxRangeValue();

      if (maxValue <= minValue) {
        maxValue = Math.min(maxAllowed, minValue + GAP);
        maxRange.value = String(maxValue);
      }

      updateUI();
      syncHiddenInputs(false);
    }

    function onChangeCommit() {
      updateUI();
      syncHiddenInputs(true);
    }

    minRange.addEventListener('input', onMinInput);
    maxRange.addEventListener('input', onMaxInput);
    minRange.addEventListener('change', onChangeCommit);
    maxRange.addEventListener('change', onChangeCommit);

    updateUI();
    syncHiddenInputs(false);

    priceRange.dataset.sliderReady = 'true';
  }

  function initAllPriceSliders(root = document) {
    root.querySelectorAll(SELECTOR).forEach(initPriceSlider);
  }

  function boot() {
    initAllPriceSliders(document);

    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) {
            shouldInit = true;
            break;
          }
        }

        if (shouldInit) break;
      }

      if (shouldInit) {
        initAllPriceSliders(document);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    initAllPriceSliders(document);
  });
})();